import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  TrophyIcon,
  TicketIcon,
  SparklesIcon,
  GiftIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import { useLanguage } from '../../contexts/LanguageContext';

const ease = [0.23, 1, 0.32, 1];

function formatDate(value, locale) {
  if (!value) return '';
  // Backend sends naive UTC ISO; treat it as UTC and format in UTC so the
  // displayed calendar day matches the campaign's local boundary date.
  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}

function statusClasses(status) {
  switch (status) {
    case 'won':
      return 'bg-success-100 text-success-700 ring-success-200';
    case 'lost':
      return 'bg-secondary-100 text-secondary-500 ring-secondary-200';
    default:
      return 'bg-primary-100 text-primary-700 ring-primary-200';
  }
}

function PromotionCard({ promotion, copy, locale, onInvite }) {
  const { progress, my_ticket: myTicket } = promotion;
  const required = progress?.required || promotion.required_referrals || 1;
  const current = progress?.referrals || 0;
  const pct = Math.min(100, Math.round((current / required) * 100));
  const remaining = progress?.remaining ?? Math.max(required - current, 0);
  const PrizeIcon = promotion.prize_type === 'cash' ? CurrencyDollarIcon : GiftIcon;

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease }}
      className="card-premium overflow-hidden h-full"
    >
      <div className="flex h-full flex-col">
        <div className="relative flex aspect-[16/9] items-center justify-center overflow-hidden bg-gradient-to-br from-orange-50 via-white to-amber-50">
          {promotion.image_url ? (
            <img
              src={promotion.image_url}
              alt={promotion.prize_name}
              className="h-full w-full object-contain p-6"
              loading="lazy"
            />
          ) : (
            <span className="icon-tile h-16 w-16 from-orange-500 to-amber-500">
              <PrizeIcon className="h-8 w-8 text-white" />
            </span>
          )}
          {myTicket ? (
            <span className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-success-500 px-3 py-1 text-2xs font-bold uppercase tracking-wide text-white shadow-sm">
              <TicketIcon className="h-3.5 w-3.5" />
              {copy.statusActive}
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className="text-2xs font-semibold uppercase tracking-[0.18em] text-amber-600">
            {promotion.current_cycle?.label || copy.activeNow}
          </p>
          <h3 className="mt-1 text-xl font-bold text-secondary-900">{promotion.title}</h3>
          <p className="mt-1 text-sm font-medium text-orange-600">{promotion.prize_name}</p>
          {promotion.subtitle ? (
            <p className="mt-3 text-sm leading-relaxed text-secondary-600">{promotion.subtitle}</p>
          ) : null}

          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-semibold text-secondary-600">
              <span>{copy.progressText.replace('{current}', current).replace('{required}', required)}</span>
              <span>{Math.min(current, required)}/{required}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary-100">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6, ease }}
                className={`h-full rounded-full ${myTicket ? 'bg-success-500' : 'bg-gradient-to-r from-orange-500 to-amber-500'}`}
              />
            </div>
            <p className={`mt-2 text-sm ${remaining === 0 ? 'font-semibold text-success-600' : 'text-secondary-600'}`}>
              {remaining === 0
                ? copy.qualified
                : remaining === 1
                  ? copy.remainingOne
                  : copy.remainingMany.replace('{count}', remaining)}
            </p>
          </div>

          {promotion.current_cycle ? (
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-2xs font-medium uppercase tracking-wide text-secondary-400">
              <span>{copy.endsOn.replace('{date}', promotion.current_cycle.ends_on || formatDate(promotion.current_cycle.ends_at, locale))}</span>
              <span>{copy.drawOn.replace('{date}', promotion.current_cycle.draw_on || formatDate(promotion.current_cycle.draw_at, locale))}</span>
            </div>
          ) : null}

          <Button
            type="button"
            onClick={onInvite}
            variant="primary"
            size="compact"
            className="mt-5 w-full min-h-[44px] justify-center"
            icon={SparklesIcon}
            iconPosition="left"
          >
            {copy.inviteCta}
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

function TicketCard({ ticket, copy, locale }) {
  const statusLabel = ticket.status === 'won'
    ? copy.statusWon
    : ticket.status === 'lost'
      ? copy.statusLost
      : copy.statusActive;
  return (
    <div className="card-premium interactive-lift flex items-center gap-4 p-4 sm:p-5">
      <div className="icon-tile h-11 w-11 shrink-0 from-orange-500 to-amber-500">
        <TicketIcon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-secondary-900">{ticket.promotion_title}</p>
        <p className="truncate text-xs text-secondary-500">
          {copy.cycleLabel}: {ticket.cycle_label} · {formatDate(ticket.awarded_at, locale)}
        </p>
        <p className="mt-1 font-mono text-xs font-semibold tracking-wide text-secondary-700">
          {ticket.ticket_code}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-3 py-1 text-2xs font-bold uppercase tracking-wide ring-1 ${statusClasses(ticket.status)}`}>
        {statusLabel}
      </span>
    </div>
  );
}

function ClientPromotionsView({ onOpenReferrals, onSeen }) {
  const { t, locale } = useLanguage();
  const copy = t.promotions || {};
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await axios.get('/api/client/promotions', { withCredentials: true });
      setPayload(response.data || null);
      setError(false);
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  useEffect(() => {
    let cancelled = false;
    axios
      .post('/api/client/promotions/seen', {}, { withCredentials: true })
      .then(() => {
        if (!cancelled) onSeen?.();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onSeen]);

  const promotions = payload?.promotions || [];
  const tickets = payload?.my_tickets || [];
  const winners = payload?.winners || [];
  const hasWinners = Boolean(payload?.has_winners) && winners.length > 0;

  if (loading) {
    return <ListPageSkeleton />;
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        eyebrow={copy.eyebrow || 'Elevate Hispano'}
        icon={TrophyIcon}
        title={copy.title || 'Promotions'}
        subtitle={copy.subtitle}
        action={(
          <Button
            type="button"
            variant="secondary"
            size="compact"
            onClick={fetchPromotions}
            icon={ArrowPathIcon}
            className="justify-center"
          >
            {t.dashboard?.refresh || 'Refresh'}
          </Button>
        )}
      />

      {error ? (
        <EmptyState
          compact
          icon={TrophyIcon}
          title={copy.loadError || 'Could not load promotions'}
          action={(
            <Button type="button" variant="primary" size="compact" onClick={fetchPromotions}>
              {t.dashboard?.retry || 'Retry'}
            </Button>
          )}
        />
      ) : promotions.length === 0 ? (
        <EmptyState
          icon={TrophyIcon}
          title={copy.noPromotions || 'No active promotions'}
          description={copy.noPromotionsDescription}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:gap-6">
          {promotions.map((promotion) => (
            <PromotionCard
              key={promotion.id}
              promotion={promotion}
              copy={copy}
              locale={locale}
              onInvite={onOpenReferrals}
            />
          ))}
        </div>
      )}

      {hasWinners ? (
        <section className="space-y-4 border-t border-secondary-200/80 pt-6">
          <div>
            <p className="eyebrow">{copy.winners || 'Winners'}</p>
            <h3 className="mt-1 text-xl font-bold tracking-tight text-secondary-900 sm:text-2xl">
              {copy.winners || 'Winners'}
            </h3>
            <p className="mt-1 text-sm text-secondary-600">{copy.winnersSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {winners.map((winner, index) => (
              <motion.div
                key={`${winner.cycle_label}-${winner.promotion_title}-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.04, ease }}
                className={`card-premium flex items-center gap-4 p-4 ${winner.is_me ? 'ring-2 ring-success-400' : ''}`}
              >
                <div className="icon-tile h-11 w-11 shrink-0 from-amber-500 to-orange-500">
                  <TrophyIcon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-secondary-900">{winner.winner_display}</p>
                  <p className="truncate text-xs text-secondary-500">
                    {winner.prize_name} · {winner.cycle_label}
                  </p>
                </div>
                {winner.is_me ? (
                  <span className="shrink-0 rounded-full bg-success-100 px-3 py-1 text-2xs font-bold uppercase tracking-wide text-success-700 ring-1 ring-success-200">
                    {copy.youWon}
                  </span>
                ) : null}
              </motion.div>
            ))}
          </div>
        </section>
      ) : null}

      {tickets.length > 0 ? (
        <section className="space-y-4 border-t border-secondary-200/80 pt-6">
          <div>
            <p className="eyebrow">{copy.myTickets || 'My tickets'}</p>
            <h3 className="mt-1 text-xl font-bold tracking-tight text-secondary-900 sm:text-2xl">
              {copy.myTickets || 'My tickets'}
            </h3>
            <p className="mt-1 text-sm text-secondary-600">{copy.myTicketsSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {tickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} copy={copy} locale={locale} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default ClientPromotionsView;
